import { Pin, PinOff } from '@tunasong/icons'
import type { FC } from 'react'
import { ListItemIcon, ListItemText, MenuItem, type MenuItemProps } from '../primitives/index.js'
import type { FavoriteControlProps } from './favorite-props.js'
import { useFavorites } from './favorites.hook.js'

type PinMenuItemProps = FavoriteControlProps<MenuItemProps>

/**
 * Dashboard Phase 2 — entity-menu affordance that toggles pin state.
 *
 * - Pin: gated by `canPinMore` (cap of 3 enforced by `applyPin`). When the cap is
 *   reached and the entity is not yet pinned, the item is disabled and the icon
 *   stays neutral so the user sees the affordance exists but understands it's not
 *   currently available.
 * - Unpin: always enabled when the entity is already pinned (the cap doesn't
 *   apply — we're freeing a slot, not consuming one).
 *
 * Used inside the entity MoreVert menu next to `<FavoriteMenuItem>`.
 */
export const PinMenuItem: FC<PinMenuItemProps> = props => {
  const { onClick, entity, ...restProps } = props
  const { isPinned, pin, unpin, canPinMore, pinCap } = useFavorites()

  const pinned = isPinned(entity.id)
  const disabled = !pinned && !canPinMore

  const label = pinned
    ? 'Unpin from dashboard'
    : disabled
      ? `Pin limit reached (${pinCap})`
      : 'Pin to dashboard'

  const Icon = pinned ? PinOff : Pin

  return (
    <MenuItem
      disabled={disabled}
      onClick={ev => {
        ev.preventDefault()
        ev.stopPropagation()
        if (pinned) {
          unpin(entity.id)
        } else {
          pin(entity)
        }
        if (onClick) {
          onClick(ev)
        }
      }}
      {...restProps}
    >
      <ListItemIcon>
        <Icon color={pinned ? 'var(--tuna-palette-primary-main)' : 'currentColor'} />
      </ListItemIcon>
      <ListItemText primary={label} />
    </MenuItem>
  )
}

export default PinMenuItem
